import { Toggle } from "@9amhealth/shared";
import { useBloc as useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useState } from "react";
import AssumedIdentityCubit from "src/state/AssumedIdentityCubit/AssumedIdentityCubit";
import { authenticationState } from "src/state/state";
import {
  Bar,
  BlockedList,
  BlockedToggle,
  ConfirmButton,
  ConfirmRow,
  DragHandle,
  ExitButton,
  ModeBadge,
  Row,
  UserId
} from "./AssumedIdentityBar.styled";
import { useDragOffset } from "./useDragOffset";

const AssumedIdentityBar: FC = () => {
  const [
    { identity, writeMode, blockedCount, blockedRequests },
    { setWriteMode }
  ] = useBlocNext(AssumedIdentityCubit);
  const [listExpanded, setListExpanded] = useState(false);
  const [confirmingWrites, setConfirmingWrites] = useState(false);
  const { targetRef, handleProps } = useDragOffset<HTMLDivElement>();

  if (!identity) {
    return null;
  }

  // Inline instead of window.confirm: automated browsers dismiss native dialogs instantly.
  const handleWriteModeChange = (enabled: boolean) => {
    if (enabled) {
      setConfirmingWrites(true);
      return;
    }
    setWriteMode(false);
  };

  const confirmWriteMode = () => {
    setConfirmingWrites(false);
    setWriteMode(true);
  };

  const modeLabel = writeMode ? "Write mode" : "Read-only";
  const showBlockedList = listExpanded && blockedRequests.length > 0;

  return (
    <Bar
      ref={targetRef}
      role="status"
      aria-label="Assumed identity"
      $writeMode={writeMode}
      $expanded={showBlockedList || confirmingWrites}
      data-testid="assumed-identity-bar"
    >
      <Row>
        <DragHandle aria-hidden title="Drag to move" {...handleProps}>
          ⠿
        </DragHandle>
        <span>
          Acting as <UserId title={identity.userId}>{identity.userId}</UserId>
        </span>
        <ModeBadge>{modeLabel}</ModeBadge>
        {blockedCount > 0 && (
          <BlockedToggle
            type="button"
            aria-expanded={listExpanded}
            onClick={() => setListExpanded(!listExpanded)}
          >
            {blockedCount} blocked
          </BlockedToggle>
        )}
        <Toggle
          aria-label="Allow writes"
          isSelected={writeMode}
          onChange={handleWriteModeChange}
        >
          Allow writes
        </Toggle>
        <ExitButton
          type="button"
          onClick={() => void authenticationState.endAssumedIdentity()}
        >
          Exit
        </ExitButton>
      </Row>
      {confirmingWrites && (
        <ConfirmRow role="alert">
          <span>Writes will be saved to this member&apos;s account.</span>
          <ConfirmButton type="button" onClick={confirmWriteMode}>
            Enable writes
          </ConfirmButton>
          <ConfirmButton
            type="button"
            onClick={() => setConfirmingWrites(false)}
          >
            Cancel
          </ConfirmButton>
        </ConfirmRow>
      )}
      {showBlockedList && (
        <BlockedList aria-label="Blocked requests" reversed>
          {blockedRequests.map(({ method, path }, index) => (
            <li key={`${blockedCount - index}`}>
              {method} {path}
            </li>
          ))}
        </BlockedList>
      )}
    </Bar>
  );
};

export default AssumedIdentityBar;
